import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ChevronLeft, ChevronRight, Search, Wallet, Info, Lock, Calculator,
  RefreshCw, FunctionSquare, Users, TrendingDown, TrendingUp, Coins,
  Briefcase, GraduationCap, Camera, Sparkles,
} from "lucide-react";
import { monthLabel } from "@/lib/attendance";
import EditableCell from "@/components/EditableCell";
import { Skeleton } from "@/components/ui/skeleton";

/* -------------------------------------------------------------------------- */
/*  Iter 44.8 — Google-Sheets-style column formulas                           */
/*  (unchanged logic; only the shell around it is redesigned in iter 45.1)    */
/* -------------------------------------------------------------------------- */

const DEFAULT_FORMULAS = {
  total_leaves: "CL + ML",
  paid_days: "min(30, max(0, 30 - TotalLeaves + CLTaken + MLTaken))",
  // Iter 45.49 — first-month proration: employees who joined in the
  // payroll month get 30 - (join_day - 1) working days; others get 30.
  total_working_days: "30 - DaysBeforeJoining",
  to_credit: "PaidDays * (Salary / 30)",
};

const FORMULA_VARS  = ["CL", "ML", "HalfDays", "Salary", "TotalLeaves", "PaidDays", "TotalWorkingDays", "ToCredit", "CLTaken", "MLTaken", "DaysBeforeJoining"];
const FORMULA_FUNCS = ["min", "max"];
const FORMULA_ALLOWED_IDS = [...FORMULA_VARS, ...FORMULA_FUNCS];

function evalFormula(expr, ctx) {
  const clean = String(expr || "").replace(/^=+/, "").trim();
  if (!clean) return { ok: true, val: 0 };
  if (!/^[0-9a-zA-Z_+\-*/(),. ]+$/.test(clean)) return { ok: false, err: "Invalid characters" };
  const idents = clean.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
  for (const id of idents) {
    if (!FORMULA_ALLOWED_IDS.includes(id)) return { ok: false, err: `Unknown identifier: ${id}` };
  }
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...FORMULA_VARS, ...FORMULA_FUNCS, `"use strict"; return (${clean});`);
    const varArgs  = FORMULA_VARS.map((id) => Number(ctx[id]) || 0);
    const funcArgs = [Math.min, Math.max];
    const val = fn(...varArgs, ...funcArgs);
    if (!Number.isFinite(val)) return { ok: false, err: "Not a number" };
    return { ok: true, val };
  } catch (e) {
    return { ok: false, err: e.message || "Error" };
  }
}

const COMPUTE_ORDER = [
  ["total_leaves",       "TotalLeaves"],
  ["paid_days",          "PaidDays"],
  ["total_working_days", "TotalWorkingDays"],
  ["to_credit",          "ToCredit"],
];

function computeRow(row, formulas) {
  const CL = Number(row.cl) || 0;
  const ML = Number(row.ml) || 0;
  const ctx = {
    CL, ML,
    HalfDays: Number(row.half_days) || 0,
    Salary: Number(row.salary) || 0,
    TotalLeaves: 0, PaidDays: 0, TotalWorkingDays: 0, ToCredit: 0,
    CLTaken: CL > 0 ? 1 : 0,
    MLTaken: ML > 0 ? 1 : 0,
    // Iter 45.49 — days of the payroll month before the employee joined
    // (0 unless this is their first month). Mirrors the backend ctx.
    DaysBeforeJoining: Number(row.days_before_joining) || 0,
  };
  const errors = {};
  for (const [key, ctxKey] of COMPUTE_ORDER) {
    const r = evalFormula(formulas[key], ctx);
    if (r.ok) ctx[ctxKey] = r.val;
    else errors[key] = r.err;
  }
  return {
    _total_leaves:       Number(ctx.TotalLeaves.toFixed(2)),
    _paid_days:          Number(ctx.PaidDays.toFixed(2)),
    _total_working_days: Number(ctx.TotalWorkingDays.toFixed(2)),
    _to_credit:          Number(ctx.ToCredit.toFixed(2)),
    _errors: errors,
  };
}

/* -------------------------------------------------------------------------- */
/*  Visual helpers — colored initials, role palette, profile pill             */
/* -------------------------------------------------------------------------- */

function stringHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}

// Curated pastel palette — matches the PLUTO purple/indigo/emerald tone.
const AVATAR_PALETTE = [
  "bg-gradient-to-br from-violet-500 to-purple-600",
  "bg-gradient-to-br from-indigo-500 to-blue-600",
  "bg-gradient-to-br from-sky-500 to-cyan-600",
  "bg-gradient-to-br from-emerald-500 to-teal-600",
  "bg-gradient-to-br from-amber-500 to-orange-600",
  "bg-gradient-to-br from-rose-500 to-pink-600",
  "bg-gradient-to-br from-fuchsia-500 to-purple-600",
  "bg-gradient-to-br from-lime-500 to-emerald-600",
];

function InitialsAvatar({ name, size = 36 }) {
  const trimmed = (name || "U").trim();
  const initials = trimmed.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const bg = AVATAR_PALETTE[stringHash(trimmed) % AVATAR_PALETTE.length];
  return (
    <div
      className={`${bg} rounded-full flex items-center justify-center text-white font-semibold shrink-0 shadow-sm ring-2 ring-white`}
      style={{ height: size, width: size, fontSize: Math.round(size * 0.4) }}
      aria-label={trimmed}
    >
      {initials}
    </div>
  );
}

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  department_admin: "Dept Admin",
  content_manager: "Manager",
  team_lead: "Team Lead",
  associate: "Associate",
};

const ROLE_PILL = {
  super_admin:      "bg-purple-100 text-purple-700 border-purple-200",
  admin:            "bg-indigo-100 text-indigo-700 border-indigo-200",
  department_admin: "bg-violet-100 text-violet-700 border-violet-200",
  content_manager:  "bg-blue-100 text-blue-700 border-blue-200",
  team_lead:        "bg-sky-100 text-sky-700 border-sky-200",
  associate:        "bg-slate-100 text-slate-600 border-slate-200",
};

function RolePill({ role }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${ROLE_PILL[role] || ROLE_PILL.associate}`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function ProfileBadge({ profile }) {
  if (profile === "intern") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
        <GraduationCap className="h-3 w-3" /> Intern
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
      <Briefcase className="h-3 w-3" /> Full time
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  KPI hero card                                                             */
/* -------------------------------------------------------------------------- */

function KPICard({ label, value, icon: Icon, tint = "slate", accent, testId }) {
  const palette = {
    slate:   { bg: "bg-white",              ring: "border-slate-200",   iconBg: "bg-slate-100 text-slate-600", label: "text-slate-400", val: "text-slate-900" },
    indigo:  { bg: "bg-white",              ring: "border-indigo-200",  iconBg: "bg-indigo-50 text-indigo-600", label: "text-indigo-500", val: "text-indigo-900" },
    rose:    { bg: "bg-white",              ring: "border-rose-200",    iconBg: "bg-rose-50 text-rose-600",     label: "text-rose-500",   val: "text-rose-700"   },
    emerald: { bg: "bg-gradient-to-br from-emerald-50 to-white", ring: "border-emerald-200", iconBg: "bg-emerald-100 text-emerald-700", label: "text-emerald-600", val: "text-emerald-800" },
  }[tint] || {};
  return (
    <div
      className={`${palette.bg} border ${palette.ring} rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow transition-shadow`}
      data-testid={testId}
    >
      <div className={`h-10 w-10 rounded-xl ${palette.iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${palette.label}`}>{label}</div>
        <div className={`text-2xl font-bold ${palette.val} tabular-nums leading-tight`}>{value}</div>
        {accent && <div className="text-[10px] text-slate-400 mt-0.5">{accent}</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Column formula popover — now with a permanently visible chip so admins    */
/*  can discover the feature without hovering.                                */
/* -------------------------------------------------------------------------- */

function ColumnFormulaPopover({ field, title, formula, canEdit, onSave, invalidRows }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(formula || "");
  useEffect(() => setDraft(formula || ""), [formula, open]);
  const save = async () => {
    const next = draft.trim();
    if (next === (formula || "").trim()) { setOpen(false); return; }
    await onSave(field, next);
    setOpen(false);
  };
  const hasIssue = invalidRows > 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-0.5 h-4 px-1 rounded text-[9px] font-semibold uppercase tracking-wider transition ${
            hasIssue
              ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
              : "bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
          }`}
          title={hasIssue ? `${invalidRows} row(s) failed the formula — click to fix` : "Column formula"}
          data-testid={`formula-btn-${field}`}
        >
          <FunctionSquare className="h-2.5 w-2.5" />
          <span>ƒx</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end" sideOffset={6} data-testid={`formula-popover-${field}`}>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
            <Calculator className="h-3.5 w-3.5 text-purple-600" />
            Column formula · <span className="text-slate-500 font-normal normal-case">{title}</span>
          </div>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-purple-500 select-none">=</span>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canEdit) { e.preventDefault(); save(); }
                if (e.key === "Escape") { setDraft(formula || ""); setOpen(false); }
              }}
              className={`font-mono text-xs pl-6 ${hasIssue ? "border-red-300 focus-visible:ring-red-200" : "border-purple-200 focus-visible:ring-purple-200"}`}
              placeholder="e.g. CL + ML"
              autoFocus
              readOnly={!canEdit}
              data-testid={`formula-input-${field}`}
            />
          </div>
          <div className="text-[10px] text-slate-500 leading-snug">
            <span className="font-semibold text-slate-600">Names:</span>{" "}
            <code className="text-purple-700">CL</code>, <code className="text-purple-700">ML</code>, <code className="text-purple-700">HalfDays</code>, <code className="text-purple-700">Salary</code>, <code className="text-purple-700">TotalLeaves</code>, <code className="text-purple-700">PaidDays</code>, <code className="text-purple-700">TotalWorkingDays</code>, <code className="text-purple-700">CLTaken</code>, <code className="text-purple-700">MLTaken</code>.<br/>
            <span className="font-semibold text-slate-600">Functions:</span>{" "}
            <code className="text-purple-700">min(a, b, …)</code>, <code className="text-purple-700">max(a, b, …)</code>. Operators: <code>+ − × ÷ ( , )</code>.
          </div>
          {canEdit ? (
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid={`formula-cancel-${field}`}>Cancel</Button>
              <Button size="sm" onClick={save} className="bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs" data-testid={`formula-save-${field}`}>Save</Button>
            </div>
          ) : (
            <div className="text-[10px] text-slate-400 italic pt-1">You need Super Admin / Admin / HR to edit column formulas.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* -------------------------------------------------------------------------- */
/*  Table                                                                     */
/* -------------------------------------------------------------------------- */

// A vertical divider between column groups. Applied to `<th>` and matching
// `<td>` via border-left so the groupings stay perfectly aligned.
const GROUP_DIVIDER = "border-l border-slate-200";

function DerivedTH({ title, field, formulas, canEditFormulas, onFormulaSave, invalidCounts, className = "" }) {
  return (
    <th className={`text-right px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 ${className}`}>
      <div className="inline-flex items-center justify-end gap-1.5 w-full">
        <span>{title}</span>
        <ColumnFormulaPopover
          field={field}
          title={title}
          formula={formulas[field]}
          canEdit={canEditFormulas}
          onSave={onFormulaSave}
          invalidRows={invalidCounts[field] || 0}
        />
      </div>
    </th>
  );
}

function PayrollTable({
  rows, formulas, canEditFormulas, onFormulaSave, invalidCounts,
  onEditSalary, canEditSalary, onEditAttendance, totals, embedded,
}) {
  return (
    <div className={`bg-white ${embedded ? "" : "rounded-2xl border border-slate-200 shadow-sm"} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-separate border-spacing-0">
          {/* Group header row — sits above the column header for quick scanning */}
          <thead className="sticky top-0 z-10">
            <tr className="bg-gradient-to-b from-slate-50 to-white">
              <th colSpan={2} className="text-left px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-100 sticky left-0 bg-gradient-to-b from-slate-50 to-white">
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> People</span>
              </th>
              <th colSpan={2} className={`text-left px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                Role &amp; Profile
              </th>
              <th colSpan={3} className={`text-right px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                Attendance
              </th>
              <th colSpan={2} className={`text-right px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-purple-500 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Formula-driven</span>
              </th>
              <th colSpan={2} className={`text-right px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                <span className="inline-flex items-center gap-1 justify-end"><Wallet className="h-3 w-3" /> Money</span>
              </th>
              <th colSpan={4} className={`text-left px-3 pt-3 pb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                Banking
              </th>
            </tr>
            <tr className="bg-white">
              <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 sticky left-0 bg-white min-w-[220px] border-b border-slate-200">Employee</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">Emp ID</th>
              <th className={`text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200 ${GROUP_DIVIDER}`}>Role</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">Department</th>
              <th className={`text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200 ${GROUP_DIVIDER}`}>CL</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">ML</th>
              <DerivedTH title="Total Leaves" field="total_leaves" formulas={formulas} canEditFormulas={canEditFormulas} onFormulaSave={onFormulaSave} invalidCounts={invalidCounts} className="border-b border-slate-200" />
              <DerivedTH title="Paid Days" field="paid_days" formulas={formulas} canEditFormulas={canEditFormulas} onFormulaSave={onFormulaSave} invalidCounts={invalidCounts} className={`border-b border-slate-200 ${GROUP_DIVIDER}`} />
              <DerivedTH title="Working Days" field="total_working_days" formulas={formulas} canEditFormulas={canEditFormulas} onFormulaSave={onFormulaSave} invalidCounts={invalidCounts} className="border-b border-slate-200" />
              <th className={`text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200 ${GROUP_DIVIDER}`}>Salary</th>
              <DerivedTH title="To Credit" field="to_credit" formulas={formulas} canEditFormulas={canEditFormulas} onFormulaSave={onFormulaSave} invalidCounts={invalidCounts} className="border-b border-slate-200" />
              <th className={`text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200 ${GROUP_DIVIDER}`}>Bank</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">IFSC</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">A/C No.</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">UPI</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={15} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                      <Wallet className="h-7 w-7 text-slate-400" />
                    </div>
                    <div className="text-sm font-medium text-slate-500">No payroll rows for this filter</div>
                    <div className="text-xs">Try clearing the search or department filter.</div>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r, idx) => {
              const errs = r._errors || {};
              const errCls = (k) => errs[k] ? "text-red-500" : "";
              const zebra = idx % 2 === 0 ? "bg-white" : "bg-slate-50/40";
              return (
                <tr
                  key={r.user_id}
                  className={`group ${zebra} hover:bg-purple-50/40 transition-colors`}
                  data-testid={`payroll-row-${r.user_id}`}
                >
                  {/* Employee — avatar + name */}
                  <td className={`px-3 py-2.5 sticky left-0 min-w-[220px] border-b border-slate-100 ${zebra} group-hover:bg-purple-50/40 transition-colors`}>
                    <div className="flex items-center gap-2.5">
                      <InitialsAvatar name={r.name} size={34} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate text-[13px]" title={r.name}>{r.name}</div>
                        <ProfileBadge profile={r.profile} />
                      </div>
                    </div>
                  </td>

                  {/* Emp ID */}
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500 whitespace-nowrap border-b border-slate-100" data-testid={`empid-${r.user_id}`}>
                    {r.employee_id ? `#${r.employee_id}` : <span className="text-slate-300">—</span>}
                  </td>

                  {/* Role */}
                  <td className={`px-3 py-2.5 whitespace-nowrap border-b border-slate-100 ${GROUP_DIVIDER}`}>
                    <RolePill role={r.role} />
                  </td>

                  {/* Department */}
                  <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap capitalize border-b border-slate-100">
                    {(r.departments || []).join(", ") || "—"}
                  </td>

                  {/* CL */}
                  <td className={`px-3 py-2.5 text-right text-slate-700 tabular-nums border-b border-slate-100 ${GROUP_DIVIDER}`}>
                    <EditableCell
                      value={r.cl}
                      readOnly={!r.can_edit_attendance}
                      onSave={(v) => onEditAttendance(r.user_id, { cl: v })}
                      testId={`cl-${r.user_id}`}
                    />
                  </td>

                  {/* ML */}
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums border-b border-slate-100">
                    <EditableCell
                      value={r.ml}
                      readOnly={!r.can_edit_attendance}
                      onSave={(v) => onEditAttendance(r.user_id, { ml: v })}
                      testId={`ml-${r.user_id}`}
                    />
                  </td>

                  {/* Total Leaves */}
                  <td className={`px-3 py-2.5 text-right tabular-nums border-b border-slate-100 ${errCls("total_leaves") || "text-slate-700"}`} data-testid={`total-leaves-${r.user_id}`} title={errs.total_leaves || ""}>
                    {errs.total_leaves ? "#ERR" : r._total_leaves}
                  </td>

                  {/* Paid Days */}
                  <td className={`px-3 py-2.5 text-right tabular-nums border-b border-slate-100 ${GROUP_DIVIDER} ${errCls("paid_days") || "text-slate-700"}`} data-testid={`paid-days-${r.user_id}`} title={errs.paid_days || ""}>
                    {errs.paid_days ? "#ERR" : r._paid_days}
                  </td>

                  {/* Working Days */}
                  <td className={`px-3 py-2.5 text-right tabular-nums border-b border-slate-100 ${errCls("total_working_days") || "text-slate-500"}`} data-testid={`total-working-days-${r.user_id}`} title={errs.total_working_days || ""}>
                    {errs.total_working_days ? "#ERR" : r._total_working_days}
                  </td>

                  {/* Salary */}
                  <td className={`px-3 py-2.5 text-right tabular-nums font-medium text-slate-700 border-b border-slate-100 ${GROUP_DIVIDER}`}>
                    <EditableCell
                      value={r.salary}
                      prefix="₹"
                      readOnly={!canEditSalary}
                      onSave={(v) => onEditSalary(r.user_id, v)}
                      testId={`salary-${r.user_id}`}
                    />
                  </td>

                  {/* To Credit */}
                  <td className={`px-3 py-2.5 text-right tabular-nums font-bold text-[13px] border-b border-slate-100 ${errs.to_credit ? "text-red-500" : "text-emerald-700"}`} data-testid={`credit-${r.user_id}`} title={errs.to_credit || ""}>
                    {errs.to_credit ? "#ERR" : `₹${r._to_credit.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                  </td>

                  {/* Banking — sourced from `PATCH /api/profile/me`. HR sees
                      whatever the person filled into their profile. Empty
                      cells render as a soft placeholder so the sheet keeps
                      column-alignment. */}
                  <td className={`px-3 py-2.5 border-b border-slate-100 ${GROUP_DIVIDER}`} data-testid={`bank-name-${r.user_id}`}>
                    <span className={r.bank_name ? "text-slate-700 text-xs" : "text-slate-300 italic text-xs"}>{r.bank_name || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100" data-testid={`bank-ifsc-${r.user_id}`}>
                    <span className={r.ifsc ? "text-slate-700 font-mono text-[11px] tracking-wider" : "text-slate-300 italic text-xs"}>{r.ifsc || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100" data-testid={`bank-account-${r.user_id}`}>
                    <span className={r.account_number ? "text-slate-700 font-mono text-[11px]" : "text-slate-300 italic text-xs"}>{r.account_number || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100" data-testid={`bank-upi-${r.user_id}`}>
                    <span className={r.upi_id ? "text-slate-700 text-xs" : "text-slate-300 italic text-xs"}>{r.upi_id || "—"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>

          {/* Sticky totals footer */}
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-gradient-to-b from-white to-slate-50 border-t-2 border-slate-200">
                <td className="px-3 py-3 sticky left-0 bg-gradient-to-b from-white to-slate-50 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">Totals</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{rows.length} member{rows.length !== 1 ? "s" : ""}</div>
                </td>
                <td className="px-3 py-3"></td>
                <td className={`px-3 py-3 ${GROUP_DIVIDER}`}></td>
                <td className="px-3 py-3"></td>
                <td className={`px-3 py-3 ${GROUP_DIVIDER}`}></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3 text-right text-slate-700 font-semibold tabular-nums" data-testid="totals-total-leaves">{totals.total_leaves.toFixed(1)}</td>
                <td className={`px-3 py-3 ${GROUP_DIVIDER}`}></td>
                <td className="px-3 py-3"></td>
                <td className={`px-3 py-3 text-right text-slate-800 font-bold tabular-nums ${GROUP_DIVIDER}`} data-testid="totals-salary">
                  ₹{totals.salary.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </td>
                <td className="px-3 py-3 text-right text-emerald-700 font-extrabold text-[13px] tabular-nums" data-testid="totals-to-credit">
                  ₹{totals.to_credit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </td>
                {/* Banking totals row — intentionally blank (no aggregation). */}
                <td className={`px-3 py-3 ${GROUP_DIVIDER}`}></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function PayrollSkeleton() {
  return (
    <div className="space-y-3 p-4" data-testid="payroll-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-2.5 w-56" />
          </div>
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function Payroll({ embedded = false, forceUserId = null }) {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [rows, setRows] = useState([]);
  const [snapshotMeta, setSnapshotMeta] = useState({ closed_at: null, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [formulas, setFormulas] = useState(DEFAULT_FORMULAS);
  const [formulasCanEdit, setFormulasCanEdit] = useState(false);

  const isHR = (user?.departments || [user?.department]).includes("hr");
  const canEditSalary = user && (["super_admin", "admin"].includes(user.role) || isHR);

  const mk = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  const minMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth() - 2, 1), []); // eslint-disable-line
  const maxMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), []); // eslint-disable-line
  const canGoPrev = cursor > minMonthDate;
  const canGoNext = cursor < maxMonthDate;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([
        api.get(`/payroll?month=${mk}`),
        api.get("/payroll-formulas"),
      ]);
      setRows(r.data?.rows || []);
      setSnapshotMeta({
        closed_at: r.data?.snapshot_closed_at || null,
        updated_at: r.data?.snapshot_updated_at || null,
      });
      setFormulas({
        total_leaves:       f.data.total_leaves       || DEFAULT_FORMULAS.total_leaves,
        paid_days:          f.data.paid_days          || DEFAULT_FORMULAS.paid_days,
        total_working_days: f.data.total_working_days || DEFAULT_FORMULAS.total_working_days,
        to_credit:          f.data.to_credit          || DEFAULT_FORMULAS.to_credit,
      });
      setFormulasCanEdit(!!f.data.can_edit);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [mk]);

  useEffect(() => { load(); }, [load]);

  const onEditSalary = async (uid, salary) => {
    const prev = rows.find((r) => r.user_id === uid)?.salary;
    setRows((all) => all.map((x) => (x.user_id === uid ? { ...x, salary: Number(salary) || 0 } : x)));
    try {
      await api.patch(`/payroll/${uid}?month=${mk}`, { salary });
      toast.success("Salary saved");
    } catch (e) {
      setRows((all) => all.map((x) => (x.user_id === uid ? { ...x, salary: prev } : x)));
      toast.error(formatApiError(e));
    }
  };

  const onEditAttendance = async (uid, patch) => {
    const prev = rows.find((r) => r.user_id === uid);
    if (!prev) return;
    const clean = {};
    if (patch.cl != null) clean.cl = Number(patch.cl) || 0;
    if (patch.ml != null) clean.ml = Number(patch.ml) || 0;
    setRows((all) => all.map((x) => (x.user_id === uid ? { ...x, ...clean } : x)));
    try {
      await api.patch(`/payroll/${uid}?month=${mk}`, clean);
      toast.success("Attendance override saved");
    } catch (e) {
      setRows((all) => all.map((x) => (x.user_id === uid ? { ...x, cl: prev.cl, ml: prev.ml } : x)));
      toast.error(formatApiError(e));
    }
  };

  const onFormulaSave = async (field, value) => {
    const prev = formulas[field];
    setFormulas((f) => ({ ...f, [field]: value }));
    try {
      await api.patch("/payroll-formulas", { [field]: value });
      toast.success("Formula updated");
    } catch (e) {
      setFormulas((f) => ({ ...f, [field]: prev }));
      toast.error(formatApiError(e));
    }
  };

  const shift = (delta) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
    if (next < minMonthDate || next > maxMonthDate) return;
    setCursor(next);
  };

  const filtered = useMemo(() => {
    let list = forceUserId ? rows.filter((r) => r.user_id === forceUserId) : rows;
    if (deptFilter !== "all") list = list.filter((r) => (r.departments || []).includes(deptFilter));
    if (profileFilter !== "all") list = list.filter((r) => r.profile === profileFilter);
    const qs = q.trim().toLowerCase();
    if (qs) list = list.filter((r) => (r.name || "").toLowerCase().includes(qs));
    return list;
  }, [rows, q, deptFilter, profileFilter, forceUserId]);

  const computedRows = useMemo(
    () => filtered.map((r) => ({ ...r, ...computeRow(r, formulas) })),
    [filtered, formulas],
  );

  const invalidCounts = useMemo(() => {
    const c = {};
    for (const r of computedRows) {
      for (const k of Object.keys(r._errors || {})) c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [computedRows]);

  const totals = useMemo(() => {
    return computedRows.reduce((acc, r) => {
      acc.salary += Number(r.salary) || 0;
      acc.to_credit += Number(r._to_credit) || 0;
      acc.total_leaves += Number(r._total_leaves) || 0;
      acc.paid_days += Number(r._paid_days) || 0;
      return acc;
    }, { salary: 0, to_credit: 0, total_leaves: 0, paid_days: 0 });
  }, [computedRows]);

  const avgPaidDays = computedRows.length ? (totals.paid_days / computedRows.length) : 0;

  return (
    <div className={embedded ? "" : "max-w-[1500px] mx-auto p-6 space-y-5"}>
      {!embedded && (
        <>
          {/* Title + month picker */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600">
                  <Wallet className="h-5 w-5" />
                </span>
                Payroll
              </h1>
              <p className="text-sm text-slate-500 mt-1 ml-[52px]">
                Google-Sheets-style column formulas · CL / ML sourced from attendance · Salary editable by Admin / HR.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Rounded segmented month picker */}
              <div className="inline-flex items-center bg-white rounded-full border border-slate-200 shadow-sm p-0.5">
                <button
                  onClick={() => shift(-1)}
                  disabled={!canGoPrev}
                  className={`p-1.5 rounded-full transition ${canGoPrev ? "hover:bg-slate-100 text-slate-700" : "text-slate-300 cursor-not-allowed"}`}
                  data-testid="payroll-prev-month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-sm font-semibold text-slate-800 px-3 min-w-[140px] text-center tabular-nums" data-testid="payroll-month-label">
                  {monthLabel(cursor.getFullYear(), cursor.getMonth())}
                </div>
                <button
                  onClick={() => shift(1)}
                  disabled={!canGoNext}
                  className={`p-1.5 rounded-full transition ${canGoNext ? "hover:bg-slate-100 text-slate-700" : "text-slate-300 cursor-not-allowed"}`}
                  data-testid="payroll-next-month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              {snapshotMeta.closed_at && (
                <span
                  title={`Closed at ${new Date(snapshotMeta.closed_at).toLocaleString()} · last updated ${new Date(snapshotMeta.updated_at).toLocaleString()}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid="payroll-closed-badge"
                >
                  <Lock className="h-3 w-3" /> Closed
                </span>
              )}
              {canEditSalary && (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/payroll/snapshot?month=${mk}`);
                      toast.success("Month snapshot updated");
                      load();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-purple-600 px-2 py-1 rounded hover:bg-purple-50 transition"
                  data-testid="payroll-snapshot-btn"
                  title="Take a snapshot of this month right now"
                >
                  <Camera className="h-3 w-3" /> Snapshot now
                </button>
              )}
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPICard
              label="Members"
              value={filtered.length}
              icon={Users}
              tint="indigo"
              accent={forceUserId ? "Filtered to your row" : (filtered.length === rows.length ? "All active members" : `Filtered from ${rows.length}`)}
              testId="kpi-members"
            />
            <KPICard
              label="Total Leaves"
              value={totals.total_leaves.toFixed(1)}
              icon={TrendingDown}
              tint="rose"
              accent={`Avg paid days: ${avgPaidDays.toFixed(1)}`}
              testId="kpi-total-leaves"
            />
            <KPICard
              label="Gross Salary"
              value={`₹${totals.salary.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
              icon={Coins}
              tint="slate"
              accent="Sum of monthly salary"
              testId="kpi-gross-salary"
            />
            <KPICard
              label="To Credit"
              value={`₹${totals.to_credit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
              icon={TrendingUp}
              tint="emerald"
              accent={`${monthLabel(cursor.getFullYear(), cursor.getMonth())} · net payable`}
              testId="kpi-to-credit"
            />
          </div>

          {/* Filters bar */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member…" className="pl-9 border-slate-200" data-testid="payroll-search" />
            </div>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="payroll-dept-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                <SelectItem value="content">Content</SelectItem>
                <SelectItem value="seo">SEO</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="hr">HR</SelectItem>
              </SelectContent>
            </Select>
            <Select value={profileFilter} onValueChange={setProfileFilter}>
              <SelectTrigger className="h-9 w-36" data-testid="payroll-profile-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All profiles</SelectItem>
                <SelectItem value="full_time">Full time</SelectItem>
                <SelectItem value="intern">Intern</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={load} className="ml-auto text-xs text-slate-500 hover:text-purple-600 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-purple-50 transition" data-testid="payroll-refresh" title="Refresh from server">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>

          {/* Formula tip */}
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 flex-wrap px-1">
            <Info className="h-3 w-3" />
            Look for the <span className="inline-flex items-center gap-0.5 h-4 px-1 rounded text-[9px] font-semibold uppercase bg-purple-50 text-purple-700 border border-purple-200"><FunctionSquare className="h-2.5 w-2.5" />ƒx</span> pill in derived column headers — click to edit the column formula. CL / ML come from the attendance sheet and are editable by HR / Admin / CM / Founder.
          </div>
        </>
      )}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
          <PayrollSkeleton />
        </div>
      ) : (
        <PayrollTable
          rows={computedRows}
          formulas={formulas}
          canEditFormulas={formulasCanEdit}
          onFormulaSave={onFormulaSave}
          invalidCounts={invalidCounts}
          onEditSalary={onEditSalary}
          onEditAttendance={onEditAttendance}
          canEditSalary={canEditSalary}
          totals={totals}
          embedded={embedded}
        />
      )}
    </div>
  );
}
